import moment from "moment";
import NODE_TYPES from "../constants/nodeTypes";
import {roundFloat} from "./index";
import RouteResponsePoint from "../models/RouteResponsePoint";
import {EstimatedTimeOfArrivalCalculator} from "./EstimatedTimeOfArrivalCalculator";

const distanceRoundDecimalPlaces = 1; // округляем расстояния до 100 метров.

const splitDuration = span => {
  const duration = moment.duration(span);
  const {days, hours, minutes} = duration._data; /* eslint-disable-line no-underscore-dangle */

  return {
    days,
    hours,
    minutes,
  };
};

// Возвращает массив с пройденными путями, содержащими расстояние и тип (для отрисовки полоски с процентами маршрута).
const getTraveledRoutes = routeDetails => {
  const traveledRoutes = [];

  if (routeDetails.ferryDistance > 0) {
    traveledRoutes.push({distance: routeDetails.ferryDistance, type: 'ferry'});
  }
  if (routeDetails.winterRoadDistance > 0) {
    traveledRoutes.push({distance: routeDetails.winterRoadDistance, type: 'winterRoad'});
  }
  if (routeDetails.otherTypeDistance > 0) {
    traveledRoutes.push({distance: routeDetails.otherTypeDistance, type: 'otherType'});
  }
  if (routeDetails.commonDistance > 0) {
    traveledRoutes.push({distance: routeDetails.commonDistance, type: 'common'});
  }
  if (routeDetails.trunkDistance > 0) {
    traveledRoutes.push({distance: routeDetails.trunkDistance, type: 'trunk'});
  }
  if (routeDetails.motorwayDistance > 0) {
    traveledRoutes.push({distance: routeDetails.motorwayDistance, type: 'motorway'});
  }
  traveledRoutes.sort((a, b) => b.distance - a.distance);
  return traveledRoutes;
};

// Схлопывает сегменты левой панели для отображения только ключевых точек. 
// Данные всех промежуточных точек плюсуются в сегменты между ключевыми точками. 
export const mergeLeftPanelRouteSegmentsForKeyPoints = (routeSegments) => {
  
  const mergedSegments = [];
  let prevKeySegment = undefined;
  for (let i = 0; i < routeSegments.length; i++) {
    const routeSegment = routeSegments[i];
    const routePoint = routeSegment.routePoint;
    const isKeyPoint = RouteResponsePoint.isKeyPoint(routePoint);
    if (isKeyPoint) {
      prevKeySegment = { ...routeSegment };
      mergedSegments.push(prevKeySegment);
    } else {
      if (!prevKeySegment) {
        continue;
      }
      if (routeSegment.hasToll) {
        prevKeySegment.hasToll = true;
      }
      prevKeySegment.segmentDistance += routeSegment.segmentDistance;
      prevKeySegment.segmentDurationHours += routeSegment.segmentDurationHours;
      prevKeySegment.segmentPlatonCost += routeSegment.segmentPlatonCost;
      prevKeySegment.traveledRoutes = mergeTraveledRoutes(prevKeySegment.traveledRoutes, routeSegment.traveledRoutes);
    }
  }
  
  // пробежимся, сделаем округления
  for (let i = 0; i < mergedSegments.length; i++) {
    const mergedRouteSegment = mergedSegments[i];
    mergedRouteSegment.segmentDistance = roundFloat(mergedRouteSegment.segmentDistance, distanceRoundDecimalPlaces);
  }
  return mergedSegments;
};

const mergeTraveledRoutes = (traveledRoutesPrev, traveledRoutes) => {
  const mergedTraveledRoutes = [];
  for (let i = 0; i < traveledRoutes.length; i++) {
    const traveledRoute = traveledRoutes[i];
    let traveledRoutePrev = traveledRoutesPrev.find((element) => element.type === traveledRoute.type);
    if (traveledRoutePrev) {
      traveledRoutePrev.distance += traveledRoute.distance;
    }
    else {
      traveledRoutePrev = traveledRoute;
    }
    mergedTraveledRoutes.push(traveledRoutePrev);
  }
  return mergedTraveledRoutes;
};

/** @param {RouteInfo} routeInfo
 * @param calculations
 * @param speeds
 * @param platonRate
 * @param routeNotFound
 */
export const routeMetricsCalculator = (routeInfo, calculations, speeds, platonRate, routeNotFound) => {
  let totalDistance = 0;
  let totalPlatonCost = 0;
  let totalDurationHours = 0;
  let totalDurationSeconds = 0;
  const metersInKilometer = 1000.0;
  const secondsInHour = 3600;
  const routeSegments = [];
  const trackNamesRatings = new Map();
  
  for (let i = 0; i < routeInfo.segments.length; i++) {
    const segment = routeInfo.segments[i];
    const routeDetails = segment.routeDetails;
    const routePoint = segment.routePoint;
    const city = routePoint.city;

    const isKeyPoint = RouteResponsePoint.isKeyPoint(routePoint);
    const isCustom = (routePoint.nodeTypes & NODE_TYPES.custom) !== 0;

    const citySize = city ? city.size : 1;
    
    // объект routeSegment должен быть создан, используя переменные totalDistance и totalDurationHours 
    // до их обновления в теле цикла, т.к. отражают накопительные значения.
    const routeSegment = {
      /** @type {RouteResponsePoint} */
      routePoint: routePoint,
      country: routePoint.country, 
      region: routePoint.region,
      totalDistance: totalDistance,
      totalDurationHours: totalDurationHours,
      isKeyPoint: isKeyPoint,
      isCustom: isCustom, 
      citySize: citySize
    };

    routeSegments.push(routeSegment);

    if (!routeDetails) {
      continue;
    }

    const ferryDistance = routeDetails.ferryDistance / metersInKilometer;
    const winterRoadDistance = routeDetails.winterRoadDistance / metersInKilometer;
    const otherTypeDistance = routeDetails.otherTypeDistance / metersInKilometer;
    const commonDistance = routeDetails.commonDistance / metersInKilometer;
    const trunkDistance = routeDetails.trunkDistance / metersInKilometer;
    const motorwayDistance = routeDetails.motorwayDistance / metersInKilometer;

    const segmentDistance = roundFloat(ferryDistance + winterRoadDistance + otherTypeDistance
        + commonDistance + trunkDistance + motorwayDistance, distanceRoundDecimalPlaces);

    totalDistance = roundFloat(totalDistance + segmentDistance, distanceRoundDecimalPlaces);
    totalDurationSeconds += routeDetails.totalTime;

    const segmentDurationHours = routeDetails.totalTime / secondsInHour;

    totalDurationHours = totalDurationSeconds / secondsInHour;

    const platonDistance = roundFloat(routeDetails.platonDistance / metersInKilometer, distanceRoundDecimalPlaces);
    const platonCost = Math.ceil(platonDistance * platonRate)
    totalPlatonCost += platonCost;

    routeSegment.segmentDistance = segmentDistance;
    routeSegment.segmentDurationHours = segmentDurationHours;
    routeSegment.segmentPlatonCost = platonCost;
    routeSegment.traveledRoutes = getTraveledRoutes(routeDetails);
    routeSegment.hasToll = routeDetails.tollDistance > 0;
    routeSegment.trackNames = routeDetails.trackNames ? routeDetails.trackNames.map(q=> {
      if (q.length > 5) {
        q = q.substring(0, 5) + "...";
      }
      trackNamesRatings.set(q, trackNamesRatings.has(q) ? trackNamesRatings.get(q) + 1 : 1);
      return q;
    }) : [];
  }

  const totalFuelConsumption = Math.round((totalDistance / 100) * calculations.fuelConsumption);
  const totalFuelCosts = Math.round(totalFuelConsumption * calculations.fuelPrice);

  const totalDuration = moment.duration(totalDurationHours, 'hours');
  const {days, hours, minutes} = splitDuration(totalDuration);
  const totalHours = days * 24 + hours;

  routeSegments.forEach(routeSegment => {
    if (routeSegment.trackNames) {
      routeSegment.trackNames.sort((a, b) =>
        (trackNamesRatings.get(b) || 0) - (trackNamesRatings.get(a) || 0));
      if (routeSegment.trackNames.length > 2) {
        routeSegment.trackNames = routeSegment.trackNames.slice(0, 2);
      }
    }
  });

  if (!routeNotFound) {
    const etaCalculator = new EstimatedTimeOfArrivalCalculator(calculations);
    etaCalculator.calculate(routeSegments);
  }

  return {
    totalDistance,
    totalPlatonCost: totalPlatonCost,
    fuelPrice: calculations.fuelPrice,
    fuelConsumption: calculations.fuelConsumption,
    totalFuelCosts,
    totalFuelConsumption,
    totalHours,
    days,
    hours,
    minutes,
    routeSegments
  };
};
